import Cookies from 'js-cookie';
import { EventContext } from 'types/Analytics';
import { WlWindow } from 'typings/globals/cordova';
import Logger from 'utils/Logger';
import { isCordova } from 'utils/platform';
import { AB_TEST_COOKIE_NAME } from '~/constants';

import {
  BusinessEvent,
  BusinessEventHandler,
  Click,
  CookieConsentUpdate,
  ExperienceCardClicked,
  ExperienceCardViewed,
  PageView,
  PaymentFormType,
} from '../../businessLayer';

declare const window: WlWindow;

function getEcommerceData(context: BusinessEvent | EventContext) {
  if ('experience' in context && context.experience) {
    const adults = context.nrOfAdults ?? 0;
    const privatePrices = context.experience?.pricing?.private_price;
    const advertised =
      context.experience?.pricing?.advertised_price_origin?.amount?.value;
    const unitPrice = adults > 0 ? privatePrices?.[adults] : undefined;
    const price = (unitPrice ?? advertised ?? 0) as number;

    return {
      value: unitPrice && adults > 0 ? unitPrice * adults : (advertised ?? 0),
      currency: 'EUR',
      items: [
        {
          item_id: context.experience?.id,
          item_name: context.experience?.title,
          currency: 'EUR',
          price,
          quantity: adults > 0 ? adults : 1,
          location_id: context.experience?.itinerary?.destination,
          item_brand: context.experience?.proposition?.distributionChannel,
          item_category: context.experience?.itinerary?.tier,
          item_category2: context.experience?.itinerary?.concept,
          index: context.position,
        },
      ],
    };
  }
  if ('trip' in context && context.trip) {
    const feeAmount = context.trip?.fee_amount ?? 0;
    const serviceFee = context.trip?.price?.service_fee_amount?.value ?? 0;
    return {
      value: feeAmount + serviceFee,
      currency: 'EUR',
      transaction_id: context.trip?.id,
      shipping: serviceFee,
      items: [
        {
          item_id: context.trip?.experience_id,
          item_name: context.trip?.title,
          quantity: context.trip?.number_of_guest ?? 0,
          price: context.trip?.price_pp ?? 0,
        },
      ],
    };
  }
  return {};
}

function addDefaultParameters(inputParams: object, evt: BusinessEvent) {
  const experimentEventData = {};
  evt.experiments?.forEach((exp) => {
    experimentEventData[`experiment_${exp.id}`] = exp.assignedVariant;
  });

  // Track special store front ab test
  experimentEventData['storefront_ab_test'] =
    Cookies.get(AB_TEST_COOKIE_NAME) || 'not_in_test';

  return {
    traffic_type: evt.traffic_type,
    account_is_host: evt.account_is_host,
    account_id: evt.account_id,
    application: isCordova() ? 'app' : 'ue',
    app_platform: CORDOVA_PLATFORM,
    app_version: withlocals_version,
    ...experimentEventData,
    ...inputParams,
  };
}

const handler: BusinessEventHandler = (evt: BusinessEvent) => {
  if (!window || !window.uetq) {
    console.warn('[Bing] Bing tracker not initialized');
    return;
  }

  Logger.logAnalytics(
    '[Bing] received event',
    evt.type,
    evt,
    evt.constructor.name,
  );
  if (evt.type === 'CookieConsentUpdate') {
    const cookieConsentUpdate = evt as CookieConsentUpdate;
    window.uetq?.push?.('consent', 'update', {
      ad_storage:
        cookieConsentUpdate.advertisement ||
        cookieConsentUpdate.personalised_ads
          ? 'granted'
          : 'denied',
      analytics_storage: cookieConsentUpdate.analytics ? 'granted' : 'denied',
      ad_user_data: cookieConsentUpdate.advertisement ? 'granted' : 'denied',
      ad_personalization: cookieConsentUpdate.personalised_ads
        ? 'granted'
        : 'denied',
    });
  }

  if (evt.type === 'PageView') {
    const pageView = evt as PageView;
    const eventData = addDefaultParameters(
      {
        page_title: pageView.title,
        page_location: pageView.location,
        page_path: pageView.path,
      },
      pageView,
    );

    window.uetq?.push?.('event', 'page_view', eventData);
  }

  if (evt.type === 'ExperienceCardViewed') {
    const experienceCardViewed = evt as ExperienceCardViewed;
    const { items } = getEcommerceData(evt);
    window.uetq?.push?.(
      'event',
      'view_item',
      addDefaultParameters(
        {
          item_list_name: window.document.title,
          item_list_id:
            experienceCardViewed.analyticsListName || window.location.pathname,
          items,
        },
        experienceCardViewed,
      ),
    );
  }

  if (evt.type === 'ExperienceCardClicked') {
    const experienceCardClicked = evt as ExperienceCardClicked;
    const { items } = getEcommerceData(evt);
    window.uetq?.push?.(
      'event',
      'select_content',
      addDefaultParameters(
        {
          item_list_name: window.document.title,
          item_list_id:
            experienceCardClicked.analyticsListName || window.location.pathname,
          items,
        },
        experienceCardClicked,
      ),
    );
  }
  if (
    evt.type === 'HostSelected' ||
    evt.type === 'PaymentOptionSelected' ||
    evt.type === 'DateSelected' ||
    evt.type === 'NumberOfGuestsSelected'
  ) {
    window.uetq?.push?.(
      'event',
      'set_checkout_option',
      addDefaultParameters(
        {
          option: evt.type,
          ...getEcommerceData(evt),
        },
        evt,
      ),
    );
  }

  if (evt.type === 'BookButtonClicked') {
    window.uetq?.push?.(
      'event',
      'begin_checkout',
      addDefaultParameters(
        {
          option: evt.type,
          ...getEcommerceData(evt),
        },
        evt,
      ),
    );
  }

  if (evt.type === 'CheckoutPageViewed') {
    window.uetq?.push?.(
      'event',
      'checkout_progress',
      addDefaultParameters(
        {
          option: evt.type,
          ...getEcommerceData(evt),
        },
        evt,
      ),
    );
  }

  if (evt.type === 'PaymentFormType') {
    const paymentFormType = evt as PaymentFormType;
    window.uetq?.push?.(
      'event',
      'payment_form_type',
      addDefaultParameters(
        {
          option: paymentFormType.variant,
        },
        paymentFormType,
      ),
    );
  }

  if (evt.type === 'PaymentOptionSelected') {
    window.uetq?.push?.(
      'event',
      'set_checkout_option',
      addDefaultParameters(
        {
          option: 'p-selected',
          ...getEcommerceData(evt),
        },
        evt,
      ),
    );
  }
  if (evt.type === 'BookingConfirmed') {
    window.uetq?.push?.(
      'event',
      'purchase',
      addDefaultParameters(
        {
          option: 'payment-confirmed',
          ...getEcommerceData(evt),
        },
        evt,
      ),
    );
  }
  if (evt.type === 'Click') {
    const click = evt as Click;
    window.uetq?.push?.(
      'event',
      'click',
      addDefaultParameters(
        {
          option: click.target,
        },
        click,
      ),
    );
  }
};

export default handler;
